import React, { useCallback } from "react";
import styled from "styled-components";
import {
  Button as AsurionButton,
  breakpoints,
  Text,
  ButtonVariant,
  Icon,
} from "@trailhead/asurion-ui";
import { trackEvent } from "@trailhead/analytics";
import { useSanitizedTranslation } from "@trailhead/i18n";
import { navigateToUrl } from "single-spa";
import { PartnerType } from "@trailhead/contentful";
import { useQueryParams } from "@trailhead/smart-setup-core";
import { useMxContext } from "@trailhead/mx-context";

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-self: stretch;
  flex-direction: row;
  border: 1px solid #d5d6da;
  border-radius: 8px;

  ${breakpoints.md2Max} {
    flex-direction: column-reverse;
    justify-content: center;
    align-items: center;
  }
`;

const HeaderTitle = styled(Text)``;

const NoWrapTitle = styled(Text)`
  white-space: nowrap;
`;

const TitleContainer = styled.div`
  margin-top: 0;
  display: flex;
  flex-direction: column;
  flex: 1;
  flex-shrink: 2;
  justify-content: center;
  z-index: 1;
  padding: 1rem;

  ${breakpoints.mdMax} {
    align-items: center;
    text-align: center;
  }

  ${breakpoints.md} {
    gap: 6px;
    padding: 1.5rem;
  }
`;

const AsurionButtonWrapper = styled(AsurionButton)`
  margin-top: 1rem;
  margin-bottom: 25px;
  display: flex;
  flex: 1;
  width: 100%;
  height: auto;
  max-height: 3rem;
`;

const SelfGuidedSetupWrapper = styled.div`
  display: flex;
  width: 216px;
  z-index: 1;
  ${breakpoints.sm} {
    max-width: 350px;
  }
`;

const IconContainer = styled.div`
  padding-left: 0.75rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ImageContainer = styled.div`
  display: flex;
  height: 100%;
  justify-content: center;
`;

const Image = styled.img`
  height: 100%;
  object-fit: contain;

  max-width: 310px;
  max-height: 310px;

  ${breakpoints.md2Max} {
    max-width: 280px;
    max-height: 280px;
  }

  ${breakpoints.mdMax} {
    max-width: 250px;
    max-height: 250px;
  }
`;

interface GenericCTAProps {
  title: string;
  subTitle: string;
  buttonText: string;
  imageSrc: string;
  buttonCallback?: () => void;
  buttonVariant?: ButtonVariant;
  buttonUrl?: string;
  noWrapTitle?: string;
}

export const GenericCTA: React.FC<GenericCTAProps> = ({
  title,
  subTitle,
  buttonText,
  imageSrc,
  buttonCallback,
  buttonVariant = "default",
  buttonUrl,
  noWrapTitle,
}) => {
  const t = useSanitizedTranslation();
  const queryParams = useQueryParams();
  const { client } = useMxContext();

  const defaultCallback = useCallback(() => {
    if (buttonUrl) {
      if (client === PartnerType.ATT) {
        trackEvent("AttProtectAdvantageButtonClick");
      } else if (client === PartnerType.VERIZON) {
        trackEvent("VerizonMobileProtectButtonClick");
      }
      window.location.href = buttonUrl;
    } else {
      trackEvent("SelfGuideNavigationButtonClick");
      queryParams.set("skipPathfinder", "true");
      navigateToUrl(`/mobile-device-setup/intro?${queryParams.toString()}`);
    }
  }, [buttonUrl, client, queryParams]);

  return (
    <Container>
      <TitleContainer>
        <HeaderTitle size={5} weight="feather">
          {t(title)}
          <NoWrapTitle size={5} weight="feather">
            {t(noWrapTitle || '')}
          </NoWrapTitle>
        </HeaderTitle>
        <HeaderTitle size={3} weight="feather">
          {t(subTitle)}
        </HeaderTitle>
        <SelfGuidedSetupWrapper>
          <AsurionButtonWrapper
            size="medium"
            variant={buttonVariant}
            onClick={buttonCallback ?? defaultCallback}
          >
            <>
              {t(buttonText)}
              <IconContainer>
                <Icon
                  size="large"
                  src="ArrowRight"
                  fill={buttonVariant === "outline" ? "#000" : "#fff"}
                />
              </IconContainer>
            </>
          </AsurionButtonWrapper>
        </SelfGuidedSetupWrapper>
      </TitleContainer>
      <ImageContainer>
        <Image src={imageSrc} />
      </ImageContainer>
    </Container>
  );
};
